import { useEffect, useState, useRef, useCallback } from 'react';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { VStack } from 'ui_2';
import { InteractiveStreetViewPanorama } from '@src/components/InteractiveStreetViewPanorama.js';
import InteractiveFlatMap from '@src/components/InteractiveFlatMap';
import { getAccount, getIsConnected, useAccount } from '@puzzlehq/sdk';
import { Route as IndexRoute } from './index';
import { Route as winnings_route } from '@routes/winnings';
import {
  InitializeStatusSuccess,
  SECONDS_PER_ROUND,
  useGameStore,
} from '@src/data/useGameStore';
import { LoadingMessage } from '@src/components/LoadingMessage';
import { ErrorMessage } from '@src/components/ErrorMessage';
import { getBackendStatuses } from '@puzzlehq/frontend-utils';
import { toast } from 'react-hot-toast';
import { Header } from '@src/components/Header';
import { NewRound } from '@src/components/NewRound';
import { usePuzzleGameContext } from '@src/providers/puzzleProvider';
import { IncorrectNetworkModal } from '@src/components/IncorrectNetworkDialog';
import { MYSTERY_CITY_AUDIO_BASE_URL } from '@src/data/utils';

const gameThemeSound = `${MYSTERY_CITY_AUDIO_BASE_URL}/game.mp3`;
const timerBeepSound = `${MYSTERY_CITY_AUDIO_BASE_URL}/timerBeep.mp3`;

const NEXT_ROUND_TIMEOUT_SECONDS = 10;

// Create single audio instances outside the component
const gameAudio = new Audio(gameThemeSound);
gameAudio.loop = true;
gameAudio.volume = 0.2;

export const Route = createFileRoute('/game')({
  beforeLoad: async () => {
    try {
      const isConnected = await getIsConnected();
      if (!isConnected) {
        throw redirect({ to: IndexRoute.to });
      }
    } catch (e) {
      console.error(`Error calling getIsConnected on /game`, e);
      throw redirect({ to: IndexRoute.to });
    }

    await getBackendStatuses();
  },
  loader: async (): Promise<InitializeStatusSuccess> => {
    const accountReq = await getAccount();
    const address = accountReq.account.address;

    if (!address || address === '') {
      console.error(
        `No account, can't load new Mystery City game. User is likely not connected to dApp. Redirecting to index route.`
      );
      throw redirect({ to: IndexRoute.to });
    }

    const game = await useGameStore.getState().initializeGame(address);

    if (game.status === 'error') {
      throw redirect({ to: IndexRoute.to });
    }

    return game;
  },
  gcTime: 0,
  shouldReload: true,
  pendingComponent: () => <LoadingMessage message='Loading your game' />,
  errorComponent: function Component({ error, reset }) {
    const navigate = useNavigate();

    return (
      <ErrorMessage
        errorMessage={error.message}
        reset={reset}
        goHome={() => navigate({ to: IndexRoute.to })}
      />
    );
  },
  component: function Component() {
    const navigate = useNavigate({ from: Route.id });
    const { arcadeCoin } = usePuzzleGameContext();

    const { account } = useAccount();

    const loaderData: InitializeStatusSuccess = Route.useLoaderData();
    const answers = loaderData.answers;

    const [
      round,
      rounds,
      submitError,
      submitStatus,
      storeGuess,
      submitGame,
      submitRound,
      score,
      incrementRound,
      isMuted,
    ] = useGameStore((state) => [
      state.round,
      state.rounds,
      state.submitError,
      state.submitStatus,
      state.storeGuess,
      state.submitGame,
      state.submitRound,
      state.score,
      state.incrementRound,
      state.isMuted,
    ]);
    const answerPosition: google.maps.LatLngLiteral = answers[round ?? 0] ?? {
      lat: 0,
      lng: 0,
    };

    const [isStreetViewLoaded, setIsStreetViewLoaded] = useState(false);
    const [isGameActive, setIsGameActive] = useState(false);
    const counterInitial = SECONDS_PER_ROUND;
    const [counter, setCounter] = useState(counterInitial);
    const flatMapRef = useRef<{ setSubmit: (value: boolean) => void } | null>(
      null
    );
    const [timerBeep] = useState(() => {
      const audio = new Audio(timerBeepSound);
      audio.loop = true;
      return audio;
    });

    useEffect(() => {
      if (isStreetViewLoaded && !isGameActive) {
        setIsGameActive(true);
      }
    }, [isStreetViewLoaded]);

    useEffect(() => {
      if (isGameActive && counter <= 0) {
        flatMapRef?.current?.setSubmit(true);
        setIsGameActive(false);
        storeGuess(round ?? 0, { lat: 0, lng: 0 });
        submitRound();
      } else if (isGameActive) {
        const timer = setTimeout(() => setCounter(counter - 1), 1000);
        return () => clearTimeout(timer);
      }
    }, [counter, isGameActive]);

    useEffect(() => {
      if (
        isGameActive &&
        counter <= 10 &&
        counter > 0 &&
        timerBeep.paused &&
        !isMuted
      ) {
        timerBeep.play().catch(console.error);
      } else if (counter <= 0 || counter > 10 || !isGameActive || isMuted) {
        timerBeep.pause();
        timerBeep.currentTime = 0;
      }

      return () => {
        timerBeep.pause();
        timerBeep.currentTime = 0;
      };
    }, [counter, isGameActive, isMuted]);

    const onGameFinish = useCallback(async () => {
      if (!arcadeCoin) throw new Error('No arcade coin!');
      if (!account) throw new Error('No account!');
      if (arcadeCoin) {
        const response = await submitGame(arcadeCoin, account.address);
        if (response.status === 'success') {
          navigate({ to: winnings_route.to });
        } else {
          toast.error(`Error submitting game: ${response.message}`, {
            id: response.message,
          });
        }
      }
    }, [JSON.stringify(arcadeCoin)]);

    const [nextRoundCountdown, setNextRoundCountdown] = useState<
      number | undefined
    >(undefined); // New state for timeout remaining

    useEffect(() => {
      if (!isGameActive) {
        const timer = setTimeout(async () => {
          if (round === rounds - 1 && submitStatus !== 'loading') {
            await onGameFinish();
          } else if (round < rounds - 1) {
            incrementRound();
            flatMapRef?.current?.setSubmit(false);
          }
        }, NEXT_ROUND_TIMEOUT_SECONDS * 1000);

        setNextRoundCountdown(NEXT_ROUND_TIMEOUT_SECONDS);
        const countdown = setInterval(() => {
          setNextRoundCountdown((prev) =>
            Math.max((prev ?? NEXT_ROUND_TIMEOUT_SECONDS) - 0.5, 0)
          );
        }, 500);

        return () => {
          clearTimeout(timer);
          clearInterval(countdown);
          setNextRoundCountdown(undefined);
        };
      }
    }, [round, isGameActive, submitStatus]);

    // set game to inactive when submit, reactivate when next round
    const onSubmit = useCallback(
      (submitted: boolean) => {
        setIsGameActive(!submitted);
        if (!submitted) {
          setCounter(SECONDS_PER_ROUND);
          setIsGameActive(true);
        }
      },
      [counter]
    );

    useEffect(() => {
      if (submitError)
        toast.error(`Couldn't submit game: ${submitError}`, {
          id: submitError,
        });
    }, [submitError, account]);

    useEffect(() => {
      let playingOnHide = false;

      const handleVisibilityChange = () => {
        if (document.hidden) {
          // Store state of both audio elements
          playingOnHide = !gameAudio.paused;
          gameAudio.pause();
          timerBeep.pause();
        } else if (playingOnHide && !isMuted) {
          // Only resume game audio if it was playing before
          gameAudio.play().catch(console.error);
          // Timer beep will auto-resume based on its own conditions
        }
      };

      if (!isMuted) {
        gameAudio.play().catch(console.error);
      } else {
        gameAudio.pause();
      }

      const playAudio = () => {
        if (!isMuted) {
          gameAudio.play().catch(console.error);
        }
        document.removeEventListener('click', playAudio);
      };

      document.addEventListener('click', playAudio);
      document.addEventListener('visibilitychange', handleVisibilityChange);

      return () => {
        gameAudio.pause();
        document.removeEventListener('click', playAudio);
        document.removeEventListener(
          'visibilitychange',
          handleVisibilityChange
        );
      };
    }, [isMuted]);

    return (
      <VStack className='relative h-full flex-none'>
        <InteractiveStreetViewPanorama
          position={answerPosition}
          onLoad={() => setIsStreetViewLoaded(true)}
        />
        <div className='lg:absolute lg:bottom-0 lg:left-0 lg:z-[9999] lg:h-[50px] lg:w-[100px] lg:bg-transparent' />
        <Header
          counter={counter}
          roundsAndScore={{ round, rounds, score }}
          showInfo={false}
        />
        <NewRound />
        <InteractiveFlatMap
          ref={flatMapRef}
          nextRoundCountdown={nextRoundCountdown}
          onSubmit={onSubmit}
          onGameFinish={onGameFinish}
        />
        <IncorrectNetworkModal />
      </VStack>
    );
  },
});
